<template>
    <div>
      <div class="flex-container panel-head align-top mb-0">
        <div class="flex-item">
          <p class="mt-5">
            {{$t('INDENT_SOURCE_DEPOT')}} :
            <strong>{{order.sources[0].address.name}}</strong>
          </p>
          <p>
            {{$t('INDENT_CREATION_DATE')}} :
            <strong>{{$moment(order.created_at).format("DD-MMM-YYYY")}}</strong>
          </p>
        </div>
        <div class="flex-item text-right">
          <Button type="secondary" v-if="selectedAction === transporterActions.assign_truck" :disabled="!groupedIndents.length" @click="$emit('changeTab')">
            {{$t('INDENT_ASSIGN_VEHICLE')}}
          </Button>

          <Button type="secondary" v-else-if="selectedAction === transporterActions.reject" :disabled="!groupedIndents.length" @click="$emit('changeTab')">
            {{$t('INDENT_REJECT')}}
          </Button>
          
          <Button type="secondary" v-else-if="selectedAction === transporterActions.accept" :disabled="!groupedIndents.length" @click="acceptIndent">
            {{$t('INDENT_ACCEPT')}}
          </Button>
        </div>
      </div>
      <div class="sub-head d-flex align-item-bottom mt-5">
        {{$t('INDENT_GROUPED_INDENTS')}}
      </div>
      <Row class="row-boxed mt-10">
        <Col :span="4">
          <label>Total Indents</label>
          <h4 class="text-gradient-dark-blue">{{ groupedIndents.length }}</h4>
        </Col>
        <Col :span="4">
          <label>Total Box <span>(Each)</span></label>
          <h4 class="text-gradient-dark-blue">{{(materialsInformation && materialsInformation.quantity) || 0}}</h4>
        </Col>
        <Col :span="4">
          <label>Total Weight <span>(KG)</span></label>
          <h4 class="text-gradient-dark-blue">{{(materialsInformation && materialsInformation.weight) || 0}}</h4>
        </Col>
        <Col :span="10">
          <label>Total Volume <span>({{preferredVolumeUnit}})</span></label>
          <h4 class="text-gradient-dark-blue">{{totalVolume}}</h4>
        </Col>
      </Row>
      <div class="table-with-action">
        <Table
          :data="groupedIndents"
          :stripe="true"
          max-height="250px"
          class="table-striped table-xl blank-striped bordered my-15"
          aria-describedby="ptlGroupedIndentData"
          >
          <TableColumn :label="$t('INDENT_INDENT_ID')" width="160">
            <template slot-scope="scope">
              {{scope.row.order_no}}
            </template>
          </TableColumn>
          <TableColumn :label="$t('MDM_DESTINATION')" width="120">
            <template slot-scope="scope">
              {{displayDestination(scope, routerSourceAndDestinationType.DEPOT)}}
            </template>
          </TableColumn>
          <TableColumn :label="$t('INDENT_CONSIGNEE')" width="120">
            <template slot-scope="scope">
              {{displayDestination(scope, routerSourceAndDestinationType.CONSIGNEE)}}
            </template>
          </TableColumn>
          <TableColumn label="Invoice Number" width="140">
            <template slot-scope="scope">
              <Tooltip v-if="scope.row.invoice_number && scope.row.invoice_number !== '-'" effect="dark" placement="top">
                <div slot="content">{{scope.row.invoice_number}}</div>
                <span>{{scope.row.invoice_number.split(',')[0]}}{{scope.row.invoice_number.split(',').length > 1 ? '...' : ''}}</span>
              </Tooltip>
              <span v-else>-</span>
            </template>
          </TableColumn>
          <TableColumn label="WEIGHT(KG)" width="120">
            <template slot-scope="scope">
              {{computeWeight(scope)}}
            </template>
          </TableColumn>
          <TableColumn :label="`VOLUME(${preferredVolumeUnit})`">
            <template slot-scope="scope">
              <span>
                {{computeVolume(scope)}}
              </span>
              <span class="action-row no-bg" v-if="!order.ptl_group_id && order.order_number !== scope.row.order_no && $hasPermission('transporter_remove_indent_from_ptl_group')" @click="openPopup(scope.$index)">
                <Tooltip class="item" effect="dark" placement="top">
                  <div slot="content" class="tooltipcontent">{{$t('REMOVE')}}</div>
                  <svgicon name="icon-link-broken" width="14" height="14"/>
                </Tooltip>
              </span>
              <div v-if="showAutoGroup && scope.$index === selectedIndex" class="popover-custom popover-fixed no-border">
                <div class="popover-body bg-white w-350">
                  <div class="popover-header">
                    {{$t('INDENT_REMOVE_FROM_AUTO_GROUP')}}
                  </div>
                  <p>
                    {{$t('INDENT_REMOVED_AUTO_GROUP_CONFIRM_MSG')}}
                  </p>
                  <div class="popover-footer">
                    <Button type="secondary" size="mini" @click="showAutoGroup=false">
                      {{$t('INDENT_CANCEL')}}
                    </Button>
                    <Button type="primary" size="mini" @click="removeIndentFromGroup(selectedIndex, scope)">
                      {{$t('INDENT_CONFIRM')}}
                    </Button>
                  </div>
                </div>
              </div>

            </template>
          </TableColumn>
        </Table>
      </div>
    </div>
  </template>

<script>
import { routerSourceAndDestinationType, transporterActions } from '@/constants/indentConstants';
import VolumeFormulaConvertor from '../../../../../../common-v2/helpers/volume-formula-converter';

export default {
  props: {
    order: {
      type: Object,
      default: () => {},
    },
    ptlGroups: {
      type: Array,
      default: () => [],
    },
    selectedAction: {
      type: String,
    },
    isEligibleForPtlGrouping: {
      type: Boolean,
    },
    materialsInformation: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      transporterActions,
      routerSourceAndDestinationType,
      tableData: null,
      disableVehicle: '',
      showAutoGroup: false,
      selectedIndex: null,
      removedOrderNumbers: [],
    };
  },
  computed: {
    groupedIndents() {
      return (this.ptlGroups.length && this.ptlGroups[0].orderData) || [];
    },
    preferredVolumeUnit() {
      return this.$preferredVolumeUnit();
    },
    totalVolume() {
      const volume = (this.materialsInformation && this.materialsInformation.volume) || 0;
      return this.getConvertedClientVolume(volume);
    },
  },
  methods: {
    getConvertedClientVolume(volume) {
      return new VolumeFormulaConvertor().revertToClient(this.preferredVolumeUnit, volume);
    },
    displayDestination(scope, entityType) {
      return scope.row.destinationDetails.filter(dest => dest.type === entityType).map(dest => dest.name).join(' , ') || '-';
    },
    computeWeight(scope) {
      return (scope.row.materialDetails && scope.row.materialDetails.weight) || 0;
    },
    computeVolume(scope) {
      const volume = (scope.row.materialDetails && scope.row.materialDetails.volume) || 0;
      return this.getConvertedClientVolume(volume);
    },
    openPopup(index) {
      this.selectedIndex = index;
      this.showAutoGroup = true;
    },
    statusName(id) {
      const mainStatus = this.$statusUtil.mainStatus;
      const mainStatusId = this.$statusUtil.getMainStatusId(id);
      return mainStatus.find(status => status.id === mainStatusId) &&
          mainStatus.find(status => status.id === mainStatusId).tlabel;
    },
    getStatus(id) {
      return `order-status-${this.$statusUtil.getMainStatusName(id)}`;
    },
    async acceptIndent() {
      try {
        let orderNumbers = [];
        // ptl grouping
        if (this.isEligibleForPtlGrouping) {
          orderNumbers = this.ptlGroups.map(groupedData => groupedData.orderData
            .map(order => order.order_no.trim())).flat(1);
        }
        const result = await this.$store.dispatch('indents/accept', { orderNo: this.order.order_no, orderNumbers });

        if (result && result.message && result.message === 'PTL_GROUP_ACTION_PROGRESS') {
          this.$successMessage({ message: this.$t(result.message) });
        } else if (result && result.message) {
          this.$error({ message: this.$t(result.message) });
        }
        this.cancelAction();
        return result;
      } catch (error) {
        return error;
      }
    },
    cancelAction() {
      this.$emit('cancelAction');
    },

    async removeIndentFromGroup(selectedIndex, scope) {
      this.$emit('removeIndentGroup', selectedIndex);
      if (scope.row.materialDetails) {
        if (scope.row.materialDetails.weight && this.materialsInformation.weight) {
          this.materialsInformation.weight -= scope.row.materialDetails.weight;
        }
        if (scope.row.materialDetails.quantity && this.materialsInformation.quantity) {
          this.materialsInformation.quantity -= scope.row.materialDetails.quantity;
        }
        if (scope.row.materialDetails.volume && this.materialsInformation.volume) {
          this.materialsInformation.volume -= scope.row.materialDetails.volume;
        }
      }
      this.showAutoGroup = false;
    },
  },
};
</script>
